/**
 * Admin write schemas for WebsiteBlogPost (CMS blog section).
 *
 * These shapes back GET/POST/PATCH/DELETE/PUBLISH/UNPUBLISH on
 * /api/v1/admin/website/blogs. Naming mirrors admin-website-pages.ts:
 * <Verb><Resource>RequestSchema (input) + <Resource>DetailSchema (output).
 *
 * `bodyHtml` / `faqHtml` are Tiptap-emitted rich text; capped at 500KB as a
 * safety boundary, not a UX expectation. The publish lifecycle reuses the
 * shared WebsitePublishStatus enum.
 */

import { z } from 'zod';

import { WebsitePublishStatusSchema } from './admin-website-pages.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { paginatedResponseSchema } from './pagination.js';

// kebab-case slugs only: lowercase letters, digits, dashes.
const BlogSlugSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9-]+$/u, 'Slug must be lowercase letters, digits, or dashes');

export const CreateWebsiteBlogPostRequestSchema = z.object({
  slug: BlogSlugSchema,
  title: z.string().min(1).max(255),
  excerpt: z.string().max(1000).nullish(),
  // Rich-text body is optional at create time: the "Add blog" dialog captures
  // metadata first and the article content is written later in the editor.
  bodyHtml: z.string().max(500_000).optional(),
  metaTitle: z.string().max(255).nullish(),
  metaDescription: z.string().max(500).nullish(),
  metaKeywords: z.string().max(500).nullish(),
  faqHtml: z.string().max(500_000).nullish(),
  coverMediaId: UuidSchema.nullish(),
  secondaryMediaId: UuidSchema.nullish(),
  /** Ordered additional gallery image media ids. */
  galleryMediaIds: z.array(UuidSchema).optional(),
  /** Linked City master id (website location source, ADR-0031). */
  cityId: UuidSchema.nullish(),
  categoryId: UuidSchema.nullish(),
  destinationId: UuidSchema.nullish(),
  /** Linked Theme master id (themes_and_collections row of type "theme"). */
  themeId: UuidSchema.nullish(),
  scheduledFor: IsoDateTimeSchema.nullish(),
  /**
   * Editor's Pick toggle. true → the post shows in the website "Editor's Picks"
   * section (newest-featured first); false → not featured. The server maps this
   * to the `featuredAt` timestamp that drives ordering.
   */
  isFeatured: z.boolean().optional(),
});
export type CreateWebsiteBlogPostRequest = z.infer<typeof CreateWebsiteBlogPostRequestSchema>;

// expectedUpdatedAt is the optimistic-concurrency guard; a stale value 409s.
export const UpdateWebsiteBlogPostRequestSchema =
  CreateWebsiteBlogPostRequestSchema.partial().extend({
    expectedUpdatedAt: IsoDateTimeSchema.optional(),
  });
export type UpdateWebsiteBlogPostRequest = z.infer<typeof UpdateWebsiteBlogPostRequestSchema>;

/**
 * Blog category master CRUD (the `/blog-categories` admin master). The lean
 * story-filter master shape: name, slug, displayOrder (`sortOrder`), isActive.
 */
export const CreateWebsiteBlogCategoryRequestSchema = z.object({
  name: z.string().min(1).max(150),
  slug: BlogSlugSchema,
  // Optional (not defaulted): keeps the RHF form's input type aligned with its
  // output type; the Prisma column defaults (0 / true) fill an omitted value.
  sortOrder: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional(),
});
export type CreateWebsiteBlogCategoryRequest = z.infer<
  typeof CreateWebsiteBlogCategoryRequestSchema
>;

// expectedUpdatedAt is the optimistic-concurrency guard; a stale value 409s.
export const UpdateWebsiteBlogCategoryRequestSchema =
  CreateWebsiteBlogCategoryRequestSchema.partial().extend({
    expectedUpdatedAt: IsoDateTimeSchema.optional(),
  });
export type UpdateWebsiteBlogCategoryRequest = z.infer<
  typeof UpdateWebsiteBlogCategoryRequestSchema
>;

/** A category row for the admin master grid. `postCount` = live posts using it. */
export const WebsiteBlogCategoryListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  postCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteBlogCategoryListItem = z.infer<typeof WebsiteBlogCategoryListItemSchema>;

export const WebsiteBlogCategoryListResponseSchema = paginatedResponseSchema(
  WebsiteBlogCategoryListItemSchema,
);
export type WebsiteBlogCategoryListResponse = z.infer<typeof WebsiteBlogCategoryListResponseSchema>;

/** Option shape for the blog category picker in the editor. */
export const WebsiteBlogCategoryOptionSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  name: z.string(),
});
export type WebsiteBlogCategoryOption = z.infer<typeof WebsiteBlogCategoryOptionSchema>;

/**
 * Option shape for the blog destination picker. `id` is a core `Destination`
 * master id (the one managed at /destinations-master); `label` is its name.
 */
export const WebsiteBlogDestinationOptionSchema = z.object({
  id: UuidSchema,
  label: z.string(),
});
export type WebsiteBlogDestinationOption = z.infer<typeof WebsiteBlogDestinationOptionSchema>;

/**
 * Option shape for the blog theme picker. `id` is a `ThemesAndCollections`
 * master id (a row of type "theme", managed at /marketing-tags); `label` is its
 * name.
 */
export const WebsiteBlogThemeOptionSchema = z.object({
  id: UuidSchema,
  label: z.string(),
});
export type WebsiteBlogThemeOption = z.infer<typeof WebsiteBlogThemeOptionSchema>;

/** Lightweight row for the admin list grid (no heavy bodyHtml/faqHtml). */
export const WebsiteBlogPostListItemSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  title: z.string(),
  excerpt: z.string().nullable(),
  status: WebsitePublishStatusSchema,
  categoryId: UuidSchema.nullable(),
  categoryName: z.string().nullable(),
  destinationId: UuidSchema.nullable(),
  /** Display name of the linked core `Destination` master, or null. */
  destinationName: z.string().nullable(),
  themeId: UuidSchema.nullable(),
  /** Display name of the linked Theme master, or null. */
  themeName: z.string().nullable(),
  coverMediaId: UuidSchema.nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  scheduledFor: IsoDateTimeSchema.nullable(),
  /** True when the post is an Editor's Pick (i.e. `featuredAt` is set). */
  isFeatured: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteBlogPostListItem = z.infer<typeof WebsiteBlogPostListItemSchema>;

/** Full record (includes rich-text body) for the editor. */
export const WebsiteBlogPostDetailSchema = WebsiteBlogPostListItemSchema.extend({
  bodyHtml: z.string(),
  faqHtml: z.string().nullable(),
  secondaryMediaId: UuidSchema.nullable(),
  galleryMediaIds: z.array(UuidSchema),
  cityId: UuidSchema.nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
});
export type WebsiteBlogPostDetail = z.infer<typeof WebsiteBlogPostDetailSchema>;
